import {set} from "local-storage";
import {FormattedMessage} from "react-intl";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {displaySnackbar} from "appActions/Notifications";
import {LS_AUTO_LOGOUT_DISABLED, LS_AUTO_LOGOUT_INTERVAL} from "shared/constants/LocalStorage";
import {AsyncThunkConfig} from "store";
import * as AutoLogoutConstants from "../constants/AutoLogout";
import {logout} from "../../logout/actions";

interface AutoLogoutState {
  /**
   * The remaining time in seconds before automatic logout occurs
   */
  counter: number;
  disabled: boolean;
  /**
   * The initial state is loaded from local storage. After first setting the interval, it is initialized
   */
  initialized: boolean;
  /**
   * The configured interval in seconds after which automatic logout occurs
   */
  interval: number;
}

const initialState: AutoLogoutState = {
  counter: AutoLogoutConstants.DEFAULT_INTERVAL,
  disabled: false,
  initialized: false,
  interval: AutoLogoutConstants.DEFAULT_INTERVAL
};

type SetAutoLogoutIntervalAction = PayloadAction<{
  interval: number,
  disabled: boolean
}>;

const decrementAutoLogoutCounter = createAsyncThunk<void, void, AsyncThunkConfig>(
  "decrementAutoLogoutCounter",
  (_, thunkAPI) => {
    if (thunkAPI.getState().authContext.autoLogout.counter <= 1) {
      thunkAPI.dispatch(logout()).then(() => {
        displaySnackbar(
          <FormattedMessage
            id="auto_logout.message"
            defaultMessage="You were logged out due to inactivity."
          />, {autoHideDuration: null}
        );
      });
    }
  }
);

const autoLogoutSlice = createSlice({
  // eslint-disable-next-line formatjs/no-literal-string-in-object
  name: "autoLogout",
  initialState,
  reducers: {
    /**
     * Set the current automatic logout counter back to the configured interval
     */
    resetAutoLogoutCounter(state) {
      state.counter = state.interval;
    },
    /**
     * Configure the automatic logout settings and initializes the state.
     *
     * The interval is limited by the min and max configuration. The counter is reset to the interval.
     * The interval and disabled are stored in local storage.
     */
    setAutoLogoutSettings(state, action: SetAutoLogoutIntervalAction) {
      const actualInterval = Math.max(Math.min(action.payload.interval, AutoLogoutConstants.MAX_INTERVAL), AutoLogoutConstants.MIN_INTERVAL);
      set<boolean>(LS_AUTO_LOGOUT_DISABLED, !!action.payload.disabled);
      set<number>(LS_AUTO_LOGOUT_INTERVAL, actualInterval);
      state.disabled = !!action.payload.disabled;
      state.interval = actualInterval;
      state.counter = actualInterval;
      state.initialized = true;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(decrementAutoLogoutCounter.fulfilled, (state) => {
      state.counter--;
    });
  }
});

export const {resetAutoLogoutCounter, setAutoLogoutSettings} = autoLogoutSlice.actions;

export {decrementAutoLogoutCounter};

export default autoLogoutSlice.reducer;
